import axios, { AxiosInstance, AxiosPromise, AxiosRequestConfig, AxiosResponse } from 'axios';
import qs from 'qs';

import { injectJwtTokenIntoHeaders } from '../tokenLocalStorage';

import { ParameterService } from 'services/ParametersProviderApi/ParametersService';
import { AxiosWrapper } from 'services/AxiosWrapper';
import {
  ITaskAPI,
  ICreateTaskRequest,
  ITaskSearchFilter,
  ITasksListSort,
  ICreateTaskCommentResponse,
  ISupplierTaskSearchRequest,
  ISupplierTaskSearchResponse,
  ICreateRecurringTaskRequest,
  IRecurringTaskAPI,
  IRecurringTaskSearchRequest,
  IRecurringTaskSearchResponse,
  IUpdateRecurringTaskRequest,
} from './types/TaskManagementResponse';
import { IPagination } from 'store/common/types';
import { IUploadResponse } from 'services/BackendApi';

export enum TaskManagementEndpoints {
  CATEGORIES = 'v1/categories',
  TEAMS = 'v1/teams',
  TASKS = 'v1/tasks',
  COMMENTS = 'v1/comments',
  UPLOADS = 'v1/uploads',
  RECURRING_TASKS = 'v1/recurring-tasks',
}

export class TaskManagementApiService<T extends AxiosWrapper<AxiosInstance>> {
  client: T;

  constructor(client: T) {
    this.client = client;
  }

  getTaskCategoriesInfo = async (): Promise<AxiosResponse> => {
    return this.client.get(TaskManagementEndpoints.CATEGORIES);
  };

  getTaskTeamsInfo = async (): Promise<AxiosResponse> => {
    return this.client.get(TaskManagementEndpoints.TEAMS);
  };
  
  getTasks = async ({
    pagination,
    sort,
    filter,
  }: {
    sort: ITasksListSort;
    filter?: Partial<ITaskSearchFilter>;
    pagination?: IPagination;
  }): Promise<AxiosResponse<ITaskAPI>> => {
    return this.client.post(`${TaskManagementEndpoints.TASKS}/search`, {
      sort,
      filter,
      pagination,
    });
  };

  searchSupplierTasks = async (
    request: ISupplierTaskSearchRequest
  ): Promise<AxiosResponse<ISupplierTaskSearchResponse>> => {
    return this.client.post(`${TaskManagementEndpoints.TASKS}/supplier-search`, request);
  };

  deleteTask = async (taskId: string): Promise<AxiosResponse<ITaskAPI>> => {
    return this.client.delete(`${TaskManagementEndpoints.TASKS}/${taskId}`);
  };
  
  getTask = async (taskId: string): Promise<AxiosResponse<ITaskAPI>> => {
    return this.client.get(`${TaskManagementEndpoints.TASKS}/${taskId}`);
  };

  editTask = async (taskId: string, taskToUpdate: ICreateTaskRequest): Promise<AxiosResponse<ITaskAPI>> => {
    return this.client.put(`${TaskManagementEndpoints.TASKS}/${taskId}`, taskToUpdate);
  };
  
  createTask = async (taskToCreate: ICreateTaskRequest): Promise<AxiosResponse<ITaskAPI>> => {
    return this.client.post(TaskManagementEndpoints.TASKS, taskToCreate);
  };

  // OWA-11923. 404 / NOT_FOUND while TMS has recurring tasks switched off.
  createRecurringTask = async (
    recurringTask: ICreateRecurringTaskRequest
  ): Promise<AxiosResponse<IRecurringTaskAPI>> => {
    return this.client.post(TaskManagementEndpoints.RECURRING_TASKS, recurringTask);
  };

  // OWA-11996. 404 / NOT_FOUND while TMS has recurring tasks switched off.
  searchRecurringTasks = async (
    request: IRecurringTaskSearchRequest
  ): Promise<AxiosResponse<IRecurringTaskSearchResponse>> => {
    return this.client.post(`${TaskManagementEndpoints.RECURRING_TASKS}/search`, request);
  };

  // OWA-11997. 404 / TASK_NOT_FOUND for a normal task or one the user can't see.
  getRecurringTask = async (taskId: string): Promise<AxiosResponse<IRecurringTaskAPI>> => {
    return this.client.get(`${TaskManagementEndpoints.RECURRING_TASKS}/${taskId}`);
  };

  // OWA-11997. TMS recalculates the next occurrence and sends no email.
  updateRecurringTask = async (
    taskId: string,
    recurringTask: IUpdateRecurringTaskRequest
  ): Promise<AxiosResponse<IRecurringTaskAPI>> => {
    return this.client.put(`${TaskManagementEndpoints.RECURRING_TASKS}/${taskId}`, recurringTask);
  };

  // OWA-11998. Empty 200; 409 / RECURRING_TASK_HAS_OCCURRENCES once a task was created from it.
  deleteRecurringTask = async (taskId: string): Promise<AxiosResponse<void>> => {
    return this.client.delete(`${TaskManagementEndpoints.RECURRING_TASKS}/${taskId}`);
  };

  createTaskComment = async (taskId: string, comment: string): Promise<AxiosResponse<ICreateTaskCommentResponse>> => {
    return this.client.post(`${TaskManagementEndpoints.TASKS}/${taskId}/comments`, { content: comment });
  };

  listTaskComments = async (taskId: string): Promise<AxiosResponse<ITaskAPI>> => {
    return this.client.get(`${TaskManagementEndpoints.TASKS}/${taskId}/comments`);
  };
  
  deleteComment = async (commentId: string): Promise<AxiosResponse<ITaskAPI>> => {
    return this.client.delete(`${TaskManagementEndpoints.COMMENTS}/${commentId}`);
  };

  addAttachmentToTask = async (taskId: string, name: string, url: string): Promise<AxiosResponse<IUploadResponse>> => {
    return this.client.post(`${TaskManagementEndpoints.TASKS}/${taskId}/uploads`, {
      name,
      url,
    });
  };

  listAttachmentsOfTask = async (taskId: string): Promise<AxiosResponse<IUploadResponse>> => {
    return this.client.get(`${TaskManagementEndpoints.TASKS}/${taskId}/uploads`);
  };

  deleteAttachmentOfTask = async (uploadId: string): Promise<AxiosResponse<IUploadResponse>> => {
    return this.client.delete(`${TaskManagementEndpoints.UPLOADS}/${uploadId}`);
  };
}

export const makeTaskManagementApi = (): TaskManagementApiService<AxiosWrapper<AxiosInstance>> => {
  const dynamicParameters = ParameterService.getParameters();
  const headers: any = {};

  const client = axios.create({
    baseURL: dynamicParameters.TASK_MANAGER_BASE_URL,
    headers: injectJwtTokenIntoHeaders(headers),
    withCredentials: true,
    paramsSerializer: params => {
      return qs.stringify(params);
    },
  });

  const clientWrapper = new AxiosWrapper(client);
  return new TaskManagementApiService<AxiosWrapper<AxiosInstance>>(clientWrapper);
};
